import { ExternalLink, Search } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import type { DrillDownDeal } from "@/components/deal-metrics/shared";
import {
  formatCurrency,
  formatDate,
  formatPercent,
  getPipelineName,
  getStageName,
  type Pipeline,
} from "@/components/deal-metrics/shared";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { DrillDownRequest } from "./drillable-value";

interface GpmDrillDownDeal extends DrillDownDeal {
  product_type: string | null;
  cost_per_deal: number | null;
}

interface GpmSummary {
  dealCount: number;
  totalGrossSales: number;
  totalNetSales: number;
  totalGrossProfit: number;
  gpmRatio: number;
  costEr: number;
  costOther: number;
}

interface DealDrillDownSheetProps {
  drillDown: DrillDownRequest | null;
  onClose: () => void;
  ownerId: string;
  baseParams: URLSearchParams;
  pipelines: Pipeline[];
}

export function DealDrillDownSheet({
  drillDown,
  onClose,
  ownerId,
  baseParams,
  pipelines,
}: DealDrillDownSheetProps) {
  const [deals, setDeals] = useState<DrillDownDeal[]>([]);
  const [gpmSummary, setGpmSummary] = useState<GpmSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const fetchDeals = useCallback(async () => {
    if (!drillDown) return;
    setLoading(true);
    setSearch("");
    setGpmSummary(null);
    try {
      const params = new URLSearchParams(baseParams);
      params.set("metric", drillDown.metric);
      params.set("section", drillDown.section);
      if (drillDown.month) params.set("month", drillDown.month);
      if (drillDown.quarter) params.set("quarter", drillDown.quarter);
      if (drillDown.campus) params.set("campus", drillDown.campus);
      if (drillDown.companyWide) params.set("companyWide", "true");
      if (drillDown.sources?.length) params.set("sources", drillDown.sources.join(","));
      if (drillDown.affiliateExclusions?.length)
        params.set("affiliateExclusions", drillDown.affiliateExclusions.join(","));
      if (drillDown.drOwnerId) params.set("drOwnerId", drillDown.drOwnerId);
      if (drillDown.affiliateNames?.length)
        params.set("affiliateNames", drillDown.affiliateNames.join(","));

      const res = await fetch(`/api/owner-metrics/${ownerId}/drill-down?${params}`);
      if (res.ok) {
        const data = await res.json();
        setDeals(data.deals ?? []);
        setGpmSummary(data.summary ?? null);
      }
    } finally {
      setLoading(false);
    }
  }, [drillDown, ownerId, baseParams]);

  useEffect(() => {
    if (drillDown) fetchDeals();
  }, [drillDown, fetchDeals]);

  const filtered = search
    ? deals.filter((d) => d.dealname?.toLowerCase().includes(search.toLowerCase()))
    : deals;

  const isOppMetric = drillDown?.metric === "opportunities";
  const isGpmMetric = drillDown?.metric === "gpm";

  return (
    <Sheet open={!!drillDown} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        className={`w-full ${isGpmMetric ? "sm:max-w-3xl" : "sm:max-w-2xl"} overflow-y-auto`}
      >
        <SheetHeader className="pb-4">
          <SheetTitle className="flex items-center gap-2">
            {drillDown?.label}
            {!loading && (
              <Badge variant="secondary" className="text-xs">
                {deals.length}
              </Badge>
            )}
          </SheetTitle>
        </SheetHeader>

        {isGpmMetric && gpmSummary && !loading && <GpmCalculationCard summary={gpmSummary} />}

        <div className="relative mb-4">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search deals..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9"
          />
        </div>

        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={`dd-sk-${i}`} className="h-10 w-full" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-center text-sm text-muted-foreground py-8">
            {search ? "No deals match your search." : "No deals found."}
          </p>
        ) : isGpmMetric ? (
          <GpmDealTable deals={filtered as GpmDrillDownDeal[]} />
        ) : (
          <div className="overflow-x-auto -mx-6 px-6">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30">
                  <TableHead className="text-xs">Deal Name</TableHead>
                  <TableHead className="text-xs text-right">Amount</TableHead>
                  <TableHead className="text-xs">Date</TableHead>
                  <TableHead className="text-xs">Stage</TableHead>
                  <TableHead className="text-xs">Pipeline</TableHead>
                  <TableHead className="text-xs w-[60px]">HS</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((d) => (
                  <TableRow key={d.hubspot_deal_id}>
                    <TableCell className="text-xs font-medium max-w-[180px] truncate">
                      {d.dealname ?? "—"}
                    </TableCell>
                    <TableCell className="text-xs text-right tabular-nums">
                      {formatCurrency(d.amount_in_home_currency)}
                    </TableCell>
                    <TableCell className="text-xs whitespace-nowrap">
                      {isOppMetric
                        ? formatDate(d.free_quote_form_filled)
                        : formatDate(d.order_date)}
                    </TableCell>
                    <TableCell className="text-xs">
                      {getStageName(d.dealstage, pipelines)}
                    </TableCell>
                    <TableCell className="text-xs">
                      {getPipelineName(d.pipeline, pipelines)}
                    </TableCell>
                    <TableCell className="text-xs">
                      <a
                        href={`https://app.hubspot.com/contacts/${d.hubspot_deal_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-0.5 text-brand-navy hover:underline"
                      >
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function GpmCalculationCard({ summary }: { summary: GpmSummary }) {
  return (
    <div className="mb-4 rounded-lg border bg-muted/30 px-4 py-3">
      <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
        How it's calculated
      </div>
      <div className="font-mono text-xs text-muted-foreground mb-3">
        SUM(net_price − cost_per_deal) ÷ SUM(gross_sales)
      </div>
      <dl className="grid grid-cols-3 gap-3 text-sm">
        <div>
          <dt className="text-xs text-muted-foreground">Total Gross Sales</dt>
          <dd className="font-semibold tabular-nums">{formatCurrency(summary.totalGrossSales)}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Total Gross Profit</dt>
          <dd className="font-semibold tabular-nums">{formatCurrency(summary.totalGrossProfit)}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">GPM</dt>
          <dd className="font-bold tabular-nums text-accent-foreground">
            {formatPercent(summary.gpmRatio)}
          </dd>
        </div>
      </dl>
      <div className="mt-3 pt-3 border-t text-xs text-muted-foreground">
        Cost per deal: <span className="font-medium">{formatCurrency(summary.costEr)}</span>{" "}
        Engagement Ring · <span className="font-medium">{formatCurrency(summary.costOther)}</span>{" "}
        Other ({summary.dealCount} closed deal{summary.dealCount === 1 ? "" : "s"})
      </div>
    </div>
  );
}

function GpmDealTable({ deals }: { deals: GpmDrillDownDeal[] }) {
  return (
    <div className="overflow-x-auto -mx-6 px-6">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/30">
            <TableHead className="text-xs">Deal Name</TableHead>
            <TableHead className="text-xs">Date</TableHead>
            <TableHead className="text-xs">Product Type</TableHead>
            <TableHead className="text-xs text-right">Net Price</TableHead>
            <TableHead className="text-xs text-right">Cost</TableHead>
            <TableHead className="text-xs text-right">Gross Profit</TableHead>
            <TableHead className="text-xs text-right">Margin</TableHead>
            <TableHead className="text-xs w-[40px]">HS</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {deals.map((d) => {
            const netPrice = d.net_price ?? 0;
            const grossSales = d.amount_in_home_currency ?? 0;
            const grossProfit = netPrice - (d.cost_per_deal ?? 0);
            const margin = grossSales > 0 ? grossProfit / grossSales : 0;
            return (
              <TableRow key={d.hubspot_deal_id}>
                <TableCell className="text-xs font-medium max-w-[160px] truncate">
                  {d.dealname ?? "—"}
                </TableCell>
                <TableCell className="text-xs whitespace-nowrap">
                  {formatDate(d.order_date)}
                </TableCell>
                <TableCell className="text-xs">{d.product_type ?? "—"}</TableCell>
                <TableCell className="text-xs text-right tabular-nums">
                  {formatCurrency(d.net_price)}
                </TableCell>
                <TableCell className="text-xs text-right tabular-nums text-muted-foreground">
                  {formatCurrency(d.cost_per_deal)}
                </TableCell>
                <TableCell className="text-xs text-right tabular-nums font-medium">
                  {formatCurrency(grossProfit)}
                </TableCell>
                <TableCell className="text-xs text-right tabular-nums">
                  {formatPercent(margin)}
                </TableCell>
                <TableCell className="text-xs">
                  <a
                    href={`https://app.hubspot.com/contacts/${d.hubspot_deal_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-0.5 text-brand-navy hover:underline"
                  >
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
